import React from 'react';
import Icon from '../../../components/AppIcon';

const ClientMetrics = () => {
  const metrics = [
    {
      id: 1,
      value: "40%",
      label: "Average Cost Reduction",
      description: "Compared to proprietary AI solutions",
      icon: "TrendingDown",
      color: "text-green-500"
    },
    {
      id: 2,
      value: "6 months",
      label: "Typical Implementation",
      description: "From strategy to production deployment",
      icon: "Clock",
      color: "text-blue-500"
    },
    {
      id: 3,
      value: "500+",
      label: "Organizations Transformed",
      description: "Across Fortune 500 and mid-market",
      icon: "Building2",
      color: "text-purple-500"
    },
    {
      id: 4,
      value: "95%",
      label: "Client Satisfaction",
      description: "Would recommend our approach",
      icon: "Star",
      color: "text-yellow-500"
    }
  ];

  const transformationStories = [
    {
      id: 1,
      company: "Global Manufacturing Corp",
      industry: "Manufacturing",
      challenge: "Vendor lock-in costing $2M annually",
      result: "Built internal AI platform, saved 60% in year one",
      logo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop&crop=center"
    },
    {
      id: 2,
      company: "TechFlow Solutions",
      industry: "Financial Services",
      challenge: "Limited AI customization options",
      result: "Deployed custom ML models, 3x faster insights",
      logo: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=100&h=100&fit=crop&crop=center"
    },
    {
      id: 3,
      company: "Healthcare Innovations",
      industry: "Healthcare",
      challenge: "Data sovereignty and compliance issues",
      result: "100% internal AI stack, full regulatory compliance",
      logo: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=100&h=100&fit=crop&crop=center"
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-brand-primary mb-4">
            Proven Results Across Industries
          </h2>
          <p className="text-xl text-text-secondary">
            Real transformation metrics from organizations that chose AI independence over vendor dependency
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {metrics?.map((metric) => (
            <div key={metric?.id} className="text-center group">
              <div className="bg-white rounded-2xl p-6 shadow-executive hover:shadow-executive-lg transition-all duration-300 border border-gray-100">
                <div className={`w-12 h-12 ${metric?.color} bg-gray-50 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={metric?.icon} size={24} />
                </div>
                
                <div className="text-3xl lg:text-4xl font-bold text-brand-primary mb-2">
                  {metric?.value}
                </div>
                
                <div className="text-lg font-semibold text-text-primary mb-2">
                  {metric?.label}
                </div>
                
                <div className="text-sm text-text-secondary">
                  {metric?.description}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Transformation Stories */}
        <div className="space-y-12">
          <div className="text-center">
            <h3 className="text-2xl lg:text-3xl font-bold text-brand-primary mb-4">
              Client Transformation Stories
            </h3>
            <p className="text-lg text-text-secondary">
              See how organizations like yours achieved AI independence
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {transformationStories?.map((story) => (
              <div key={story?.id} className="bg-gradient-to-br from-white to-brand-surface rounded-2xl p-8 shadow-executive hover:shadow-executive-lg transition-all duration-300 border border-gray-100">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden mr-4">
                    <img 
                      src={story?.logo} 
                      alt={`${story?.company} logo`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="font-semibold text-brand-primary">
                      {story?.company}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {story?.industry}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-text-secondary mb-1">
                      Challenge:
                    </div>
                    <div className="text-text-primary">
                      {story?.challenge}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-text-secondary mb-1">
                      Result:
                    </div>
                    <div className="text-brand-primary font-semibold">
                      {story?.result}
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center text-sm text-text-secondary">
                    <Icon name="CheckCircle" size={16} className="text-green-500 mr-2" />
                    Transformation completed in 2024
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-brand-primary to-brand-secondary rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Join These Success Stories?
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Schedule a strategic consultation to discuss your AI independence roadmap and see how we can help you achieve similar results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-brand-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200">
                Schedule Consultation
              </button>
              <button className="border border-white/30 text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors duration-200">
                View All Case Studies
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientMetrics;